import { Alert, Box, Stack, Typography } from "@mui/material";
import { getWasteStreamRegistrationSubmissionMethod } from "encamp-shared/src/submissions/wasteStreamRegistration";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useCurrentUser } from "hooks/useCurrentUser";

/** How the server dates the submission when nothing narrows the shipments. */
const DEFAULT_DUE_DATE_DESCRIPTION =
  "Due date is set automatically to the earliest shipment date for this stream plus 30 days.";

export interface RegisterNewTabProps {
  facilityName: string;
  state: string;
  /** Title the submission will be created with, from the shared title builder. */
  title: string;
  /** Notes the submission will be created with. Absent when it is filed without any. */
  notes?: string;
  /** Overrides the due-date sentence for a flow that dates the clock differently. */
  dueDateDescription?: string;
}

/**
 * What the submission will be filed as, so staff confirm against the real
 * strings rather than a description of them. Title and notes come from the
 * shared builders the resolver uses, so the preview cannot drift from what
 * lands; the method is always the one the facility's state derives.
 */
function SubmissionPreview({
  title,
  notes,
  method,
}: {
  title: string;
  notes?: string;
  method: string;
}) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        Title
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Notes
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {notes ?? "—"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Submission method
      </Typography>
      <Typography variant="body2">{method}</Typography>
    </Box>
  );
}

/**
 * "Register new" tab content: creates (and links, server-side) a fresh
 * IN_PROGRESS placeholder registration for this one stream, reusing
 * `createWasteStreamRegistrationSubmissions` (T6). Assignee, due date, and
 * submission method are all computed server-side from the facility's state;
 * this surfaces them read-only rather than collecting them, mirroring
 * `CreateWasteStreamRegistrationSubmissionsDialog` cut down to a single stream.
 *
 * Also serves the registration-update dialog, which files the same submission
 * type against an existing registration and so needs the same preview of what
 * is about to be created.
 */
export function RegisterNewTab({
  facilityName,
  state,
  title,
  notes,
  dueDateDescription = DEFAULT_DUE_DATE_DESCRIPTION,
}: RegisterNewTabProps) {
  const { user } = useCurrentUser();
  const method = prettyPrintEnumValue(getWasteStreamRegistrationSubmissionMethod(state));

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        {dueDateDescription} Assignee: {user?.email ?? "you"}.
      </Alert>

      <Typography variant="body2" color="text.secondary">
        Facility: {facilityName} · State: {state}
      </Typography>

      <SubmissionPreview title={title} notes={notes} method={method} />
    </Stack>
  );
}
